import { Link, Outlet } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../app/providers/AuthProvider'

const MarketingLayout = () => {
  const { token, user, logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [hoverPos, setHoverPos] = useState({ x: -100, y: -100 })
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })
  const [isHovering, setIsHovering] = useState(false)
  const menuRef = useRef(null)
  const gridRef = useRef(null)

  useEffect(() => {
    const handleClick = (event) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(event.target)) setMenuOpen(false)
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    const moveCursor = (event) => {
      setCursorPos({ x: event.clientX, y: event.clientY })
      const target = event.target
      const clickable = target.closest('a') || target.closest('button') || target.closest('input')
      setIsHovering(Boolean(clickable))
    }

    window.addEventListener('mousemove', moveCursor)
    return () => window.removeEventListener('mousemove', moveCursor)
  }, [])

  const handleMouseMove = (event) => {
    if (!gridRef.current) return
    const rect = gridRef.current.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    setHoverPos({ x: Math.floor(x / 64) * 64, y: Math.floor(y / 64) * 64 })
  }

  return (
    <div
      className={`relative min-h-screen overflow-hidden bg-surface text-ink custom-cursor-active ${isHovering ? 'cursor-hover' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverPos({ x: -100, y: -100 })}
      ref={gridRef}
    >
      <div
        className="custom-cursor-dot"
        style={{ left: `${cursorPos.x}px`, top: `${cursorPos.y}px`, transform: 'translate(-50%, -50%)' }}
      />
      <div
        className="custom-cursor-ring"
        style={{ left: `${cursorPos.x}px`, top: `${cursorPos.y}px`, transform: 'translate(-50%, -50%)' }}
      />

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-grid" aria-hidden="true" />
        <div
          className="absolute h-[64px] w-[64px] bg-interactive-box transition-opacity duration-500"
          style={{ left: `${hoverPos.x}px`, top: `${hoverPos.y}px`, opacity: hoverPos.x >= 0 ? 1 : 0 }}
        />
        <div className="absolute -right-32 -top-40 h-96 w-96 rounded-full bg-glow-cyan float-slow" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-glow-mint float-slow" aria-hidden="true" />
      </div>

      <nav className="relative z-30 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" className="glass-panel flex items-center rounded-2xl px-3 py-2">
          <img src="/logo.jpeg" alt="Flynt logo" className="h-12 w-24 bg-surface object-contain" />
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          className="glass-panel flex h-10 w-10 items-center justify-center rounded-full text-lg md:hidden"
        >
          {mobileOpen ? 'X' : '='}
        </button>

        <div className="hidden items-center gap-6 text-sm font-medium md:flex">
          <Link to="/" className="transition hover:text-slate-900 hover:underline">Home</Link>
          <Link to="/features" className="transition hover:text-slate-900 hover:underline">Features</Link>
          <Link to="/how-it-works" className="transition hover:text-slate-900 hover:underline">How it works</Link>
          <Link to="/pricing" className="transition hover:text-slate-900 hover:underline">Pricing</Link>
          <Link to="/security" className="transition hover:text-slate-900 hover:underline">Security</Link>
          <Link to="/docs" className="transition hover:text-slate-900 hover:underline">Docs</Link>

          {!token ? (
            <Link to="/login" className="brand-button rounded-full px-5 py-2.5 text-sm font-semibold">Sign in</Link>
          ) : (
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="glass-panel flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold text-slate-700"
              >
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </button>
              {menuOpen ? (
                <div className="glass-panel absolute right-0 z-40 mt-2 w-44 rounded-2xl">
                  <Link to="/app/profile" className="block px-4 py-2 text-sm text-slate-700 hover:bg-white/60">Profile</Link>
                  <Link to="/app" className="block px-4 py-2 text-sm text-slate-700 hover:bg-white/60">Dashboard</Link>
                  <button
                    type="button"
                    onClick={() => {
                      logout()
                      setMenuOpen(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-white/60"
                  >
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </nav>

      {mobileOpen ? (
        <div className="relative z-20 mx-6 mb-4 rounded-2xl border border-white/70 bg-white/80 px-6 py-4 shadow-lg backdrop-blur md:hidden">
          <div className="flex flex-col gap-4 text-sm font-medium">
            <Link to="/" className="text-slate-700">Home</Link>
            <Link to="/features" className="text-slate-700">Features</Link>
            <Link to="/how-it-works" className="text-slate-700">How it works</Link>
            <Link to="/pricing" className="text-slate-700">Pricing</Link>
            <Link to="/security" className="text-slate-700">Security</Link>
            <Link to="/docs" className="text-slate-700">Docs</Link>
            {!token ? (
              <div className="flex flex-col gap-3">
                <Link to="/login" className="brand-button rounded-full px-4 py-2 text-center">Sign in</Link>
                <Link to="/signup" className="rounded-full border border-slate-300 px-4 py-2 text-center">Create account</Link>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Link to="/app" className="rounded-full border border-slate-300 px-4 py-2 text-center">Dashboard</Link>
                <Link to="/app/profile" className="rounded-full border border-slate-300 px-4 py-2 text-center">Profile</Link>
                <button type="button" onClick={logout} className="rounded-full border border-slate-300 px-4 py-2 text-slate-700">
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <main className="relative z-10 w-full">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-slate-200/80 bg-white/70 backdrop-blur">
        <div className="mx-auto w-full max-w-6xl px-6 py-12">
          <div className="grid gap-8 md:grid-cols-[2fr_1fr_1fr_1fr]">
            <div>
              <img src="/logo.jpeg" alt="Flynt logo" className="h-14 w-28 bg-surface object-contain" />
              <p className="mt-4 text-sm text-slate-600">
                Zero-config deploys for full-stack teams. Ship faster with a clean control plane.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Product</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li><Link to="/app/deployments" className="hover:text-slate-800">Deployments</Link></li>
                <li><Link to="/app/projects" className="hover:text-slate-800">Projects</Link></li>
                <li><Link to="/pricing" className="hover:text-slate-800">Pricing</Link></li>
                <li>Status</li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Developers</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li>CLI install</li>
                <li>API docs</li>
                <li>Changelog</li>
                <li>Guides</li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Company</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li><Link to="/security" className="hover:text-slate-800">Security & Trust</Link></li>
                <li>About</li>
                <li>Careers</li>
                <li>Privacy</li>
                <li>Terms</li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-slate-200 pt-6 text-xs text-slate-500 md:flex-row">
            <span>Copyright 2026 Flynt by Inflynx. All rights reserved.</span>
            <span>Built in India | Global infra</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default MarketingLayout
